import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AngularFireModule } from '@angular/fire';
import { AngularFirestoreModule } from '@angular/fire/firestore';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { AngularFireStorageModule } from '@angular/fire/storage';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';
import { PdfViewerModule } from 'ng2-pdf-viewer';

import { environment } from '../environments/environment'
import { AppComponent } from './app.component';
import { RecordsService } from './services/records.service';
import { RecordsComponent } from './views/records/records.component';
import { AppRoutingModule } from './app-routing.module';
import { DispatchComponent } from './views/dispatch/dispatch.component';
import { RecordDetailComponent } from './views/record-detail/record-detail.component';
import { NotesComponent } from './views/notes/notes.component';
import { PatrollerFilterPipe } from './utils/patroller-filter.pipe';
import { SideMenuComponent } from './views/side-menu/side-menu.component';
import { OpeningsComponent } from './views/openings/openings.component';
import { OpeningsService } from './services/openings.service';
import { AuthService } from './services/auth.service';
import { AuthGuard } from './services/auth-guard.service';
import { LoginComponent } from './views/login/login.component';
import { NoPermissionComponent } from './views/no-permission/no-permission.component';
import { RecordDetailDialogComponent } from './views/dialogs/record-detail-dialog/record-detail-dialog.component';
import { RecordDeleteDialogComponent } from './views/dialogs/record-delete-dialog/record-delete-dialog.component';
import { RecordEditTimeDialogComponent } from './views/dialogs/record-edit-time-dialog/record-edit-time-dialog.component';
import { OpeningFilterPipe } from './utils/opening-filter.pipe';
import { NotesFilterPipe } from './utils/notes-filter.pipe';
import { KmcComponent } from './views/kmc/kmc.component';
import { UsefulLinksComponent } from './views/useful-links/useful-links.component';
import { LinksService } from './services/links.service';
import { DirectoryComponent } from './views/directory/directory.component';
import { LinkEditComponent } from './views/dialogs/link-edit/link-edit.component';
import { ContactEditComponent } from './views/dialogs/contact-edit/contact-edit.component';
import { OpeningEditComponent } from './views/dialogs/opening-edit/opening-edit.component';
import { PickFrontsideSweepComponent } from './views/dialogs/pick-frontside-sweep/pick-frontside-sweep.component';
import { RosterComponent } from './views/roster/roster.component';
import { RosterEditComponent } from './views/dialogs/roster-edit/roster-edit.component';
import { TransactionService } from './services/transaction.service';
import { TenThirtythreeHomeComponent } from './views/ten-thirtythree-home/ten-thirtythree-home.component';
import { TenThirtythreeDetailComponent } from './views/ten-thirtythree-detail/ten-thirtythree-detail.component';
import { LiftEvacHomeComponent } from './views/lift-evac-home/lift-evac-home.component';
import { LiftEvacDetailComponent } from './views/lift-evac-detail/lift-evac-detail.component';
import { LiftEvacNewComponent } from './views/dialogs/lift-evac-new/lift-evac-new.component';
import { ReportingComponent } from './views/reporting/reporting.component';
import { QueryService } from './services/query.service';
import { WigComponent } from './views/wig/wig.component';
import { JoinComponent } from './views/join/join.component';
import { DashboardComponent } from './views/dashboard/dashboard.component';
import { AddMorningNoteComponent } from './views/dialogs/add-morning-note/add-morning-note.component';
import { TrainingLinkEditComponent } from './views/dialogs/training-link-edit/training-link-edit.component';
import { MedMinuteComponent } from './views/med-minute/med-minute.component';
import { ForgotPasswordComponent } from './views/forgot-password/forgot-password.component';
import { AddExpiredMedComponent } from './views/dialogs/add-expired-med/add-expired-med.component';
import { ExpiredMedsComponent } from './views/expired-meds/expired-meds.component';
import { WeatherModule } from '../weather/weather.module';

@NgModule({
  declarations: [
    AppComponent,
    RecordsComponent,
    DispatchComponent,
    RecordDetailComponent,
    NotesComponent,
    PatrollerFilterPipe,
    SideMenuComponent,
    OpeningsComponent,
    LoginComponent,
    NoPermissionComponent,
    RecordDetailDialogComponent,
    RecordDeleteDialogComponent,
    RecordEditTimeDialogComponent,
    OpeningFilterPipe,
    NotesFilterPipe,
    KmcComponent,
    UsefulLinksComponent,
    DirectoryComponent,
    LinkEditComponent,
    ContactEditComponent,
    OpeningEditComponent,
    PickFrontsideSweepComponent,
    RosterComponent,
    RosterEditComponent,
    TenThirtythreeHomeComponent,
    TenThirtythreeDetailComponent,
    LiftEvacHomeComponent,
    LiftEvacDetailComponent,
    LiftEvacNewComponent,
    ReportingComponent,
    WigComponent,
    JoinComponent,
    DashboardComponent,
    AddMorningNoteComponent,
    TrainingLinkEditComponent,
    MedMinuteComponent,
    ForgotPasswordComponent,
    AddExpiredMedComponent,
    ExpiredMedsComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    AppRoutingModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule,
    AngularFireAuthModule,
    AngularFireStorageModule,
    MatButtonModule, MatInputModule, MatRadioModule, MatSelectModule, 
    MatSidenavModule, MatIconModule, MatListModule, MatDatepickerModule,
    MatCheckboxModule, MatDialogModule, MatMenuModule, MatTableModule, MatSlideToggleModule,
    MatMomentDateModule, MatChipsModule, MatAutocompleteModule, MatGridListModule, MatCardModule,
    MatTooltipModule, MatSnackBarModule, MatExpansionModule, MatButtonToggleModule, 
    MatProgressSpinnerModule, MatTabsModule, MatProgressBarModule, MatSortModule,
    BrowserAnimationsModule,
    ReactiveFormsModule, 
    PdfViewerModule,
    WeatherModule
  ],
  entryComponents: [
    RecordDetailDialogComponent,
    RecordDeleteDialogComponent,
    RecordEditTimeDialogComponent,
    LinkEditComponent,
    ContactEditComponent,
    OpeningEditComponent,
    PickFrontsideSweepComponent,
    RosterEditComponent,
    LiftEvacNewComponent,
    AddMorningNoteComponent,
    TrainingLinkEditComponent,
    AddExpiredMedComponent
  ],
  providers: [
    RecordsService,
    OpeningsService,
    AuthService,
    AuthGuard,
    LinksService,
    TransactionService,
    QueryService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
